import React from 'react';
import {ProjectDataToStore} from '@packages/systems/designer/canvas';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {withOwnTriggerTuples} from '../engines/IX3PreviewEngine/IX3EngineUtility';

/**
 * Based on the data received from Designer App, initialize IX3 on the page.
 * It will initialize interactions only once per page loaded, so if we expect
 * this to support realtime update for IX3 we need to change logic here.
 */
export const useInitializeIX3OnSeparateCanvasOrigin = ({
  interactions,
  timelines,
  isLoading,
  pageId,
}: {
  interactions: ProjectDataToStore['interactions'] | undefined;
  timelines: ProjectDataToStore['timelines'] | undefined;
  pageId: ProjectDataToStore['pageId'] | undefined;
  isLoading: boolean;
}) => {
  const ix3IsInitializedForPage = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (window.Webflow?.require && interactions && timelines && pageId) {
      // Skip initialization if either:
      // • Page is the same (we already initialized)
      // • Page is still loading
      if (ix3IsInitializedForPage.current === pageId || isLoading) return;

      const ix3SiteModule = window.Webflow.require('ix3');

      if (ix3SiteModule) {
        ix3SiteModule.ready().then(() => {
          const instance = ix3SiteModule.getInstance();
          // Mirrors the publish snippet (getIx3Js) and the same-origin preview
          // path (IX3EngineUtility.registerEngineState) so the separate-canvas
          // origin plays what publishes.
          instance?.setFeatures({
            timelineGroups: getFeatureFlagOutsideOfReact(
              FEATURE_FLAGS.IX3_TIMELINE_GROUPS
            ),
          });
          // Fresh trigger tuples per registration: the structured-clone
          // transfer keeps a duplicated interaction's shared tuple aliased, so
          // a fired Exit intent or Duration would spend both.
          instance?.register(withOwnTriggerTuples(interactions), timelines);
        });
      }

      // storying the pageId for which we initialized interactions, so we don't do it again
      ix3IsInitializedForPage.current = pageId;
    }
  }, [pageId, isLoading, interactions, timelines]);
};
